import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate, useParams } from "react-router-dom";
import { AuthProvider, useAuth } from "@/lib/auth";
import { ThemeProvider } from "@/lib/theme";
import { Toaster } from "sonner";
import { useEffect, useState } from "react";
import api from "@/lib/api";

import PublicHome from "@/pages/PublicHome";
import { TrackersList, TrackerDetail } from "@/pages/Trackers";
import { SnapshotsList, SnapshotDetail, SnapshotWaveDetail } from "@/pages/Snapshots";
import { SentimentList, SentimentDetail } from "@/pages/Sentiment";
import Watchlist from "@/pages/Watchlist";
import { LoginPage, RegisterPage, ChangePasswordPage, ForgotPasswordPage, ResetPasswordPage } from "@/pages/Auth";
import { PanelistLayout, PanelistHome, PanelistWaves, PanelistProfile, SurveyTake, PanelistRewards, PanelistPoints } from "@/pages/Panelist";
import { LanguageProvider } from "@/hooks/useLanguage";
import { AdminLayout, AdminDashboard, AdminQuestions, AdminTrackers, AdminSentiment, AdminSentimentInsights, AdminWaves, AdminWaveBuilder, AdminWaveReport, AdminRewards, AdminEditorial, AdminUsers, AdminCustomers, AdminSiteContent, AdminMyAccount, AdminHealth, AdminIntelligence, AdminReferrals } from "@/pages/Admin";
import { AdminSurveyResults, AdminSurveyResultsDetail } from "@/components/admin/AdminSurveyResults";
// AdminIncompleteProfiles is now imported inside Admin.jsx where it renders as a tab inside AdminUsers.
// SectorPage (legacy) replaced by SectorAreaPage in /app/frontend/src/pages/SectorAreaPage.jsx
import { ArticlesList, ArticleDetail } from "@/pages/Articles";
import EnterpriseDashboard from "@/pages/EnterpriseDashboard";
import { ForPatrons, ForBusinesses, Methodology } from "@/pages/WhyOodel";
import AdminAudience from "@/pages/AdminAudience";
import { AdminTeam, AdminPartners, AdminRaffle, AdminInbox, AdminSectors } from "@/pages/AdminExtras";
import { AdminEmailTemplates, AdminEmailOutbox } from "@/pages/AdminEmail";
import { AdminProfileDna, AdminMenuPermissions, AdminLegalPages } from "@/pages/AdminCMS";
import AdminBrandIntel from "@/pages/AdminBrandIntel";
import { AdminDemoAccounts } from "@/pages/AdminDemoAccounts";
import { AdminFinanceRevenue, AdminFinanceInvoices, AdminFinancePayments } from "@/pages/AdminFinance";
import JoinPatron from "@/pages/JoinPatron";
import InviteLanding from "@/pages/InviteLanding";
import CampusAmbassadorLanding from "@/pages/CampusAmbassadorLanding";
import AdminAmbassadors from "@/pages/AdminAmbassadors";
import AmbassadorDashboard from "@/pages/AmbassadorDashboard";
import AdminQualityIssues from "@/pages/AdminQualityIssues";
import { UrduLabels, WaveUrduReview } from "@/pages/UrduAdmin";
import InsightsList from "@/pages/InsightsList";
import SectorAreaPage from "@/pages/SectorAreaPage";
import PublicWaves from "@/pages/PublicWaves";
import { CustomerSentimentLanding, CustomerSentimentRate, CustomerSentimentScan, CustomerSentimentBadge, DemoAutoLogin } from "@/pages/CustomerSentiment";
import { BusinessLayout, BusinessOverview, BusinessQrManagement, BusinessPrivateInsights, BusinessOodelX, BusinessInvoices, BusinessDocuments } from "@/pages/BusinessDashboard";
import DocumentSign from "@/pages/DocumentSign";
import { AdminCSBusinesses, AdminCSBusinessDetail, AdminCSTemplates, AdminCSCategories, AdminCSPerformance, AdminCSPendingInsights } from "@/pages/AdminCustomerSentiment";
import { MobileGate } from "@/lib/useMobileAppRedirect";
import MobileOnboarding from "@/pages/MobileOnboarding";
import LegalPage from "@/pages/LegalPage";
import WaitlistLanding from "@/pages/WaitlistLanding";
import SetPassword from "@/pages/SetPassword";
import Unsubscribe from "@/pages/Unsubscribe";
import ImpersonationBanner from "@/components/ImpersonationBanner";
import CookieConsent from "@/components/CookieConsent";
import GAScript from "@/components/GAScript";

function MustChangePasswordGuard({ children }) {
  const { user } = useAuth();
  const loc = useLocation();
  const nav = useNavigate();
  useEffect(() => {
    if (user?.must_change_password && loc.pathname !== "/change-password" && loc.pathname !== "/login") {
      nav("/change-password", { replace: true });
    }
  }, [user, loc.pathname, nav]);
  return children;
}

/**
 * Whenever the homepage is hit with a ?ref=CODE query, bounce the visitor to
 * the personalized invite landing page (/invite/CODE). Unified flow per
 * design decision (d) — both waitlist-open and waitlist-closed share-link
 * recipients see the same celebratory landing, then the CTA copy + downstream
 * route diverge based on `waitlist_open`.
 */
function HomeRefRedirect({ children }) {
  const loc = useLocation();
  const ref = new URLSearchParams(loc.search).get("ref");
  if (ref) {
    return <Navigate to={`/invite/${encodeURIComponent(ref)}`} replace />;
  }
  return children;
}

/**
 * /r/:code — short peer-referral link (same code every patron receives at
 * signup; Growth Leads reuse this same URL). Just alias to /invite/:code so
 * the existing landing page + attribution pipeline handles it. Fixes the
 * blank-page bug when a Growth Lead shared her link.
 */
function InviteRedirect() {
  const { code } = useParams();
  if (!code) return <Navigate to="/" replace />;
  return <Navigate to={`/invite/${encodeURIComponent(code)}`} replace />;
}

/**
 * Swaps the homepage for the minimal Waitlist landing page when
 * `site_content.waitlist_landing_enabled` is "true".
 *
 * Strict-correctness contract: we NEVER render the wrong page. If we don't
 * know yet whether the landing is on or off, we render a neutral splash and
 * wait. There is NO timeout fallback to the homepage — falling back would
 * (re-)introduce the homepage-then-landing flash that the user explicitly
 * called out.
 *
 * Returning-visitor speed: we persist the latest decision in localStorage so
 * subsequent visits render the correct page instantly with zero network
 * dependency. The async fetch still runs in the background to refresh the
 * cache; if the admin toggles the flag, the swap takes effect on the *next*
 * page load (no mid-render flash).
 *
 * Staff (admin / pm / researcher) always bypass the gate so they can keep
 * working from any environment regardless of the public toggle state.
 */
const WAITLIST_CACHE_KEY = "oodel_waitlist_landing_v1";

function readCachedDecision() {
  // PHASE 8.44 — Boot pre-fetch beats localStorage. If `public/index.html`
  // already loaded /site-content before React mounted, derive the decision
  // directly from `window.__OODEL_BOOT__.site_content` so we never paint
  // the BrandSplash for a returning visitor.
  try {
    if (typeof window !== "undefined" && window.__OODEL_BOOT__?.site_content) {
      const sc = window.__OODEL_BOOT__.site_content;
      const on = String(sc.waitlist_landing_enabled || "false").toLowerCase() === "true";
      return on;
    }
  } catch { /* fall through */ }
  try {
    const v = localStorage.getItem(WAITLIST_CACHE_KEY);
    return v === "true" ? true : v === "false" ? false : null;
  } catch { return null; }
}

function writeCachedDecision(v) {
  try { localStorage.setItem(WAITLIST_CACHE_KEY, v ? "true" : "false"); } catch { /* swallow */ }
}

function BrandSplash() {
  // Neutral oodel. splash — cream background matches the waitlist landing
  // so the eventual swap (if any) is visually invisible. Used by both the
  // top-level WaitlistGate and the WaitlistLanding page while CMS loads.
  return (
    <div
      style={{
        position: "fixed", inset: 0, display: "flex",
        alignItems: "center", justifyContent: "center",
        background: "#F6F1E8", color: "#0a0a0a",
        fontFamily: "Inter, system-ui, sans-serif",
        zIndex: 9999,
      }}
      data-testid="oodel-brand-splash"
      aria-busy="true"
      aria-label="Loading oodel."
    >
      <div style={{ textAlign: "center", opacity: 0.85 }}>
        <div style={{ fontSize: 32, fontWeight: 700, letterSpacing: "-0.02em" }}>
          oodel<span style={{ color: "#16A34A" }}>.</span>
        </div>
      </div>
    </div>
  );
}

function WaitlistGate({ children }) {
  const { user } = useAuth();
  const initialCached = readCachedDecision();
  // If the cache already knows the answer, we render the correct page
  // immediately. If not, decision stays null until /site-content responds.
  const [decision, setDecision] = useState(initialCached);

  useEffect(() => {
    let cancelled = false;
    api.get("/site-content").then((r) => {
      if (cancelled) return;
      const on = String((r.data || {}).waitlist_landing_enabled || "false").toLowerCase() === "true";
      writeCachedDecision(on);
      // Only update state if the answer differs — avoids a render thrash
      // for returning visitors where the cache was already correct.
      setDecision((prev) => (prev === on ? prev : on));
    }).catch(() => {
      // API failed (cold start / offline). If we already had a cached
      // answer, keep using it. If we had nothing, stay on the splash —
      // we'd rather block than show the wrong page.
    });
    return () => { cancelled = true; };
  }, []);

  // Staff bypass — always render the actual app underneath the gate.
  if (user && ["admin", "pm", "researcher"].includes(user.role)) return children;

  // Don't render the wrong page. If we don't know yet, show the brand
  // splash. There is no timeout fallback by design.
  if (decision === null) return <BrandSplash />;

  return decision ? <WaitlistLanding /> : children;
}

/**
 * Scroll to top of the page whenever the route pathname changes.
 * Fixes the "I clicked to a new page and it starts halfway down" bug where
 * a user scrolls in one page (e.g. Admin → Questions) and then navigates
 * client-side to another (Admin → Waves) — without this, the new page
 * inherits the previous scroll position because the browser only resets
 * scroll on full page loads, not on client-side route changes.
 */
function ScrollToTopOnNavigate() {
  const { pathname } = useLocation();
  useEffect(() => {
    try {
      window.scrollTo({ top: 0, left: 0, behavior: "auto" });
    } catch { window.scrollTo(0, 0); }
  }, [pathname]);
  return null;
}

function App() {
  return (
    <AuthProvider>
      <ThemeProvider>
      <BrowserRouter>
        <LanguageProvider>
        <ScrollToTopOnNavigate />
        <GAScript />
        <Toaster position="top-right" richColors closeButton />
        <ImpersonationBanner />
        <CookieConsent />
        <MustChangePasswordGuard>
        <Routes>
          <Route path="/" element={<HomeRefRedirect><MobileGate><WaitlistGate><PublicHome /></WaitlistGate></MobileGate></HomeRefRedirect>} />
          <Route path="/invite/:code" element={<InviteLanding />} />
          {/* /r/:code — the peer-referral short link used by every patron
              (and Growth Leads, who reuse their own signup code). Alias to
              /invite/:code so the existing landing page + attribution pipe
              handles it. Fixes the blank page when a GL shares her link. */}
          <Route path="/r/:code" element={<InviteRedirect />} />
          <Route path="/cap/:slug" element={<CampusAmbassadorLanding />} />
          <Route path="/welcome" element={<MobileOnboarding />} />
          <Route path="/privacy" element={<LegalPage kind="privacy" />} />
          <Route path="/terms" element={<LegalPage kind="terms" />} />
          <Route path="/cookie-policy" element={<LegalPage kind="cookies" />} />
          <Route path="/trackers" element={<TrackersList />} />
          <Route path="/trackers/:id" element={<TrackerDetail />} />
          <Route path="/snapshots" element={<SnapshotsList />} />
          <Route path="/snapshots/wave/:wid" element={<SnapshotWaveDetail />} />
          <Route path="/snapshots/:id" element={<SnapshotDetail />} />
          <Route path="/waves" element={<PublicWaves />} />
          <Route path="/oodelscore" element={<CustomerSentimentLanding />} />
          <Route path="/customer-sentiment" element={<Navigate to="/oodelscore" replace />} />
          <Route path="/scan/:qrCode" element={<CustomerSentimentScan />} />
          <Route path="/rate/:qrCode" element={<CustomerSentimentScan />} />
          <Route path="/feedback/:qrCode" element={<CustomerSentimentRate />} />
          <Route path="/cs/:businessSlug" element={<CustomerSentimentBadge />} />
          <Route path="/:a/:b/demo" element={<DemoAutoLogin />} />
          <Route path="/brands" element={<Navigate to="/sentiment" replace />} />
          <Route path="/sentiment" element={<SentimentList />} />
          <Route path="/sentiment/:slug" element={<SentimentDetail />} />
          <Route path="/sectors/:key" element={<SectorAreaPage />} />
          {/* Legacy /sectors/:slug route removed — handled by SectorAreaPage now. */}
          <Route path="/articles" element={<ArticlesList />} />
          <Route path="/articles/:id" element={<ArticleDetail />} />
          <Route path="/dashboard" element={<Navigate to="/enterprise" replace />} />
          <Route path="/enterprise" element={<EnterpriseDashboard />} />
          <Route path="/for-patrons" element={<ForPatrons />} />
          <Route path="/for-businesses" element={<ForBusinesses />} />
          <Route path="/methodology" element={<Methodology />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/change-password" element={<ChangePasswordPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/set-password" element={<SetPassword />} />
          <Route path="/unsubscribe" element={<Unsubscribe />} />
          {/* Legacy waitlist landing is retired — visitors who hit /join now
              just go to the direct signup page. The old standalone landing
              is no longer mounted at any public route. */}
          <Route path="/join" element={<Navigate to="/register" replace />} />

          <Route path="/panelist" element={<PanelistLayout />}>
            <Route index element={<PanelistHome />} />
            <Route path="waves" element={<PanelistWaves />} />
            <Route path="profile" element={<PanelistProfile />} />
            <Route path="rewards" element={<PanelistRewards />} />
            <Route path="points" element={<PanelistPoints />} />
            <Route path="survey/:id" element={<SurveyTake />} />
            <Route path="trackers" element={<TrackersList />} />
            <Route path="trackers/:id" element={<TrackerDetail />} />
            <Route path="snapshots" element={<SnapshotsList />} />
            <Route path="snapshots/wave/:wid" element={<SnapshotWaveDetail />} />
            <Route path="snapshots/:id" element={<SnapshotDetail />} />
            <Route path="articles" element={<ArticlesList />} />
            <Route path="articles/:id" element={<ArticleDetail />} />
            <Route path="sentiment" element={<SentimentList />} />
            <Route path="sentiment/:slug" element={<SentimentDetail />} />
            <Route path="watchlist" element={<Watchlist />} />
            <Route path="ambassador" element={<AmbassadorDashboard />} />
            <Route path="brands" element={<Navigate to="/panelist/sentiment" replace />} />
          </Route>

          <Route path="/business" element={<BusinessLayout />}>
            <Route index element={<BusinessOverview />} />
            <Route path="oodel-x" element={<BusinessOodelX />} />
            <Route path="qr" element={<BusinessQrManagement />} />
            <Route path="documents" element={<BusinessDocuments />} />
            <Route path="invoices" element={<BusinessInvoices />} />
            <Route path="insights" element={<BusinessPrivateInsights />} />
          </Route>

          <Route path="/sign/:docId" element={<DocumentSign />} />

          <Route path="/admin" element={<AdminLayout />}>
            <Route index element={<AdminDashboard />} />
            <Route path="questions" element={<AdminQuestions />} />
            <Route path="trackers" element={<AdminTrackers />} />
            <Route path="sentiment" element={<AdminSentiment />} />
            <Route path="sentiment/:iid" element={<AdminSentimentInsights />} />
            <Route path="customer-sentiment" element={<AdminCSBusinesses />} />
            <Route path="customer-sentiment/:id" element={<AdminCSBusinessDetail />} />
            <Route path="customer-sentiment-templates" element={<AdminCSTemplates />} />
            <Route path="customer-sentiment-categories" element={<AdminCSCategories />} />
            <Route path="customer-sentiment-performance" element={<AdminCSPerformance />} />
            <Route path="customer-sentiment-insights" element={<AdminCSPendingInsights />} />
            <Route path="finance-revenue" element={<AdminFinanceRevenue />} />
            <Route path="finance-invoices" element={<AdminFinanceInvoices />} />
            <Route path="finance-payments" element={<AdminFinancePayments />} />
            <Route path="waves" element={<AdminWaves />} />
            <Route path="waves/new" element={<AdminWaveBuilder />} />
            <Route path="waves/:id/edit" element={<AdminWaveBuilder />} />
            <Route path="waves/:wid/report" element={<AdminWaveReport />} />
            <Route path="survey-results" element={<AdminSurveyResults />} />
            <Route path="survey-results/:wid" element={<AdminSurveyResultsDetail />} />
            <Route path="intelligence" element={<AdminIntelligence />} />
            {/* Legacy standalone route — now consolidated into the
                /admin/users page as a tab. Redirect old bookmarks. */}
            <Route path="incomplete-profiles" element={<Navigate to="/admin/users?tab=incomplete" replace />} />
            <Route path="rewards" element={<AdminRewards />} />
            <Route path="editorial" element={<AdminEditorial />} />
            <Route path="users" element={<AdminUsers />} />
            <Route path="referrals" element={<AdminReferrals />} />
            <Route path="ambassadors" element={<AdminAmbassadors />} />
            <Route path="customers" element={<AdminCustomers />} />
            <Route path="brands" element={<AdminBrandIntel />} />
            <Route path="audience" element={<AdminAudience />} />
            <Route path="site-content" element={<AdminSiteContent />} />
            <Route path="email-templates" element={<AdminEmailTemplates />} />
            <Route path="email-outbox" element={<AdminEmailOutbox />} />
            <Route path="my-account" element={<AdminMyAccount />} />
            <Route path="health" element={<AdminHealth />} />
            <Route path="team" element={<AdminTeam />} />
            <Route path="partners" element={<AdminPartners />} />
            <Route path="raffle" element={<AdminRaffle />} />
            <Route path="inbox" element={<AdminInbox />} />
            <Route path="sectors" element={<AdminSectors />} />
            <Route path="profile-dna" element={<AdminProfileDna />} />
            <Route path="menu-permissions" element={<AdminMenuPermissions />} />
            <Route path="legal-pages" element={<AdminLegalPages />} />
            <Route path="demo-accounts" element={<AdminDemoAccounts />} />
            <Route path="quality-issues" element={<AdminQualityIssues />} />
            {/* Urdu translation console — labels + per-wave side-by-side
                review. Wave/question Urdu editing is also available inline
                in the respective editors; this page shows everything at once. */}
            <Route path="i18n/labels" element={<UrduLabels />} />
            <Route path="i18n/waves/:wid" element={<WaveUrduReview />} />
          </Route>
          <Route path="/insights" element={<InsightsList />} />
          {/* /sectors/:key declared earlier */}
          <Route path="/join" element={<JoinPatron />} />
          <Route path="/confirm" element={<JoinPatron />} />
        </Routes>
        </MustChangePasswordGuard>
        </LanguageProvider>
      </BrowserRouter>
      </ThemeProvider>
    </AuthProvider>
  );
}

export default App;
